import React, { useState, useMemo } from 'react';
import {
  useCareOrders,
  useAcknowledgeCareOrder,
  useCompleteCareOrder,
  useBlockCareOrder,
  useUpdateCareOrder,
  type CareOrder,
  useApproveCareOrderStep,
  useRejectCareOrderStep,
  useCreateCareOrderHandoff,
  useAcceptCareOrderHandoff,
} from '@/services/api';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';
import { ClockIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import CareOrderHistoryModal from '@/components/CareOrderHistoryModal';
import Modal from '@/components/Modal';

const formatTime = (value?: string | null) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const Tasks: React.FC = () => {
  const { hasPermission } = useAuth();
  const { pushToast } = useToasts();
  
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('open');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [historyOrderId, setHistoryOrderId] = useState<string | null>(null);
  const [signoffDialog, setSignoffDialog] = useState<{ orderId: string; step: number; mode: 'approve' | 'reject'; label: string } | null>(null);
  const [signoffNote, setSignoffNote] = useState('');
  const [signoffAssignee, setSignoffAssignee] = useState('');
  const [handoffDialog, setHandoffDialog] = useState<{ orderId: string; suggestedRole?: string } | null>(null);
  const [handoffNote, setHandoffNote] = useState('');
  const [handoffRole, setHandoffRole] = useState('');

  const filters = useMemo(() => ({
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
    priority: selectedPriority !== 'all' ? selectedPriority : undefined,
    role: selectedRole !== 'all' ? selectedRole : undefined,
  }), [selectedCategory, selectedStatus, selectedPriority, selectedRole]);

  const {
    data: orders = [],
    isLoading,
    isError,
    isFallback,
    fallbackMessage,
  } = useCareOrders(undefined, filters);

  const acknowledgeOrder = useAcknowledgeCareOrder();
  const completeOrder = useCompleteCareOrder();
  const blockOrder = useBlockCareOrder();
  const updateOrder = useUpdateCareOrder();
  const approveSignoff = useApproveCareOrderStep();
  const rejectSignoff = useRejectCareOrderStep();
  const createHandoff = useCreateCareOrderHandoff();
  const acceptHandoff = useAcceptCareOrderHandoff();

  const handleAcknowledge = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限接單' });
      return;
    }
    try {
      await acknowledgeOrder.mutateAsync(orderId);
      pushToast({ tone: 'success', message: '已接單' });
    } catch (err) {
      pushToast({ tone: 'error', message: '接單失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleComplete = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限完成指示' });
      return;
    }
    const notes = prompt('完成備註（選填）：');
    if (notes === null) return; // cancelled
    try {
      await completeOrder.mutateAsync({ orderId, notes: notes || undefined });
      pushToast({ tone: 'success', message: '指示已完成' });
    } catch (err) {
      pushToast({ tone: 'error', message: '完成失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleBlock = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限封鎖指示' });
      return;
    }
    const reason = prompt('封鎖原因：');
    if (!reason) return;
    try {
      await blockOrder.mutateAsync({ orderId, reason });
      pushToast({ tone: 'success', message: '指示已封鎖' });
    } catch (err) {
      pushToast({ tone: 'error', message: '封鎖失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleInProgress = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限更新狀態' });
      return;
    }
    try {
      await updateOrder.mutateAsync({ orderId, payload: { status: 'in_progress' } });
      pushToast({ tone: 'success', message: '已開始執行' });
    } catch (err) {
      pushToast({ tone: 'error', message: '更新失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const hasSignoffPermission = hasPermission('care:signoff');
  const hasHandoffPermission = hasPermission('care:handoff');

  const openSignoffModal = (
    orderId: string,
    step: number,
    label: string,
    mode: 'approve' | 'reject',
    currentAssignee?: string,
  ) => {
    setSignoffDialog({ orderId, step, mode, label });
    setSignoffAssignee(currentAssignee ?? '');
    setSignoffNote('');
  };

  const handleSignoffSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!signoffDialog) return;
    try {
      if (signoffDialog.mode === 'approve') {
        await approveSignoff.mutateAsync({
          orderId: signoffDialog.orderId,
          step: signoffDialog.step,
          payload: {
            note: signoffNote.trim() || undefined,
            assignee: signoffAssignee.trim() || undefined,
          },
        });
        pushToast({ tone: 'success', message: '簽核完成' });
      } else {
        if (!signoffNote.trim()) {
          pushToast({ tone: 'error', message: '請輸入退回原因' });
          return;
        }
        await rejectSignoff.mutateAsync({
          orderId: signoffDialog.orderId,
          step: signoffDialog.step,
          payload: {
            note: signoffNote.trim(),
            assignee: signoffAssignee.trim() || undefined,
          },
        });
        pushToast({ tone: 'success', message: '已退回上一階段' });
      }
      setSignoffDialog(null);
      setSignoffAssignee('');
      setSignoffNote('');
    } catch (err) {
      pushToast({ tone: 'error', message: '簽核流程失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const openHandoffModal = (orderId: string, suggestedRole?: string) => {
    setHandoffDialog({ orderId, suggestedRole });
    setHandoffRole(suggestedRole ?? '');
    setHandoffNote('');
  };

  const handleHandoffSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!handoffDialog) return;
    if (!handoffNote.trim()) {
      pushToast({ tone: 'error', message: '請輸入交班備註' });
      return;
    }
    try {
      await createHandoff.mutateAsync({
        orderId: handoffDialog.orderId,
        payload: { note: handoffNote.trim(), toRole: handoffRole.trim() || undefined },
      });
      pushToast({ tone: 'success', message: '交班已建立' });
      setHandoffDialog(null);
      setHandoffNote('');
      setHandoffRole('');
    } catch (err) {
      pushToast({ tone: 'error', message: '交班建立失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleAcceptHandoff = async (orderId: string, handoffId?: string) => {
    try {
      await acceptHandoff.mutateAsync({ orderId, handoffId });
      pushToast({ tone: 'success', message: '已接班' });
    } catch (err) {
      pushToast({ tone: 'error', message: '接班失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'medication': return 'bg-blue-100 text-blue-700';
      case 'lab': return 'bg-purple-100 text-purple-700';
      case 'nursing': return 'bg-emerald-100 text-emerald-700';
      case 'followup': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Routine': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-50 text-blue-700';
      case 'acknowledged': return 'bg-cyan-50 text-cyan-700';
      case 'in_progress': return 'bg-yellow-50 text-yellow-700';
      case 'done': return 'bg-emerald-50 text-emerald-700';
      case 'blocked': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const getSignoffBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const formatSignoffStatus = (status: string) => {
    if (status === 'approved') return '已簽核';
    if (status === 'rejected') return '已退回';
    return '待簽核';
  };

  const groupedOrders = useMemo(() => {
    const groups: Record<string, CareOrder[]> = {
      'Critical': [],
      'High': [],
      'Routine': [],
    };
    orders.forEach((order) => {
      groups[order.priority]?.push(order);
    });
    return groups;
  }, [orders]);

  const pendingCount = orders.filter((o) => o.status === 'open' || o.status === 'acknowledged').length;
  const inProgressCount = orders.filter((o) => o.status === 'in_progress').length;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="section-title mb-1">Task Management</p>
          <h1 className="text-2xl font-semibold text-slate-900">Care Orders & Tasks</h1>
          <p className="text-sm text-slate-500">
            {pendingCount} pending · {inProgressCount} in progress
          </p>
        </div>
        {isFallback && (
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            Fallback{fallbackMessage ? ` · ${fallbackMessage}` : ''}
          </span>
        )}
      </header>

      {/* Filters */}
      <div className="glass-panel">
        <div className="px-6 py-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Category</label>
              <select
                className="input"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All categories</option>
                <option value="medication">Medication</option>
                <option value="lab">Lab</option>
                <option value="nursing">Nursing</option>
                <option value="followup">Follow-up</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Status</label>
              <select
                className="input"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Priority</label>
              <select
                className="input"
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                <option value="all">All priorities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Routine">Routine</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Role</label>
              <select
                className="input"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="all">All roles</option>
                <option value="RN">RN</option>
                <option value="ICU tech">ICU tech</option>
                <option value="Lab">Lab</option>
                <option value="Front desk">Front desk</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading/Error States */}
      {isLoading && (
        <div className="glass-panel p-8 text-center">
          <p className="text-slate-500">載入中...</p>
        </div>
      )}

      {isError && (
        <div className="glass-panel p-8 text-center">
          <p className="text-red-600">載入失敗，請稍後再試</p>
        </div>
      )}

      {/* Orders by Priority */}
      {!isLoading && !isError && (
        <div className="space-y-6">
          {Object.entries(groupedOrders).map(([priority, priorityOrders]) => (
            priorityOrders.length > 0 && (
              <div key={priority} className="glass-panel">
                <div className="border-b border-slate-100 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">{priority} Priority</h3>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                      {priorityOrders.length}
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {priorityOrders.map((order) => {
                    const signoffs = order.signoffs ?? [];
                    const currentStep = signoffs.find((step) => step.status !== 'approved');
                    const pendingHandoff = order.pendingHandoff ?? null;
                    const latestHandoff = order.latestHandoff ?? null;
                    const slaBadgeMinutes = signoffs.find((step) => step.slaMinutes)?.slaMinutes;
                    return (
                      <div key={order.id} className="px-6 py-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className={`pill text-[10px] ${getCategoryColor(order.category)}`}>
                              {order.category}
                            </span>
                            <span className={`pill text-[10px] ${getPriorityColor(order.priority)}`}>
                              {order.priority}
                            </span>
                            <span className={`pill text-[10px] ${getStatusColor(order.status)}`}>
                              {order.status.replace('_', ' ')}
                            </span>
                            <span className="pill bg-slate-50 text-[10px] text-slate-600">
                              {order.assignedRole}
                            </span>
                            {slaBadgeMinutes && (
                              <span className={`pill text-[10px] ${order.slaBreached ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {order.slaBreached ? 'SLA 逾時' : `SLA ${slaBadgeMinutes}m`}
                              </span>
                            )}
                          </div>
                          <h4 className="mb-1 font-semibold text-slate-900">{order.title}</h4>
                          <p className="mb-2 text-sm text-slate-600">{order.details}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                            <span>患者: {order.patientId}</span>
                            {order.assignedTo && <span>指派給: {order.assignedTo}</span>}
                            {order.dueAt && (
                              <span className="flex items-center gap-1">
                                <ClockIcon className="h-3 w-3" />
                                到期: {formatTime(order.dueAt)}
                              </span>
                            )}
                            <span>建立者: {order.createdBy}</span>
                            <span>版本: v{order.version}</span>
                          </div>

                          {signoffs.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {signoffs.map((step) => {
                                const actionable = hasSignoffPermission && currentStep && currentStep.step === step.step && step.status !== 'approved';
                                return (
                                  <div
                                    key={`${order.id}-${step.step}`}
                                    className="flex flex-wrap items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs"
                                  >
                                    <div>
                                      <p className="font-semibold text-slate-700">{step.label}</p>
                                      <p className="text-[11px] text-slate-500">指派：{step.assignee || '未指派'}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`rounded-full px-2 py-0.5 text-[10px] ${getSignoffBadgeColor(step.status)}`}>
                                        {formatSignoffStatus(step.status)}
                                      </span>
                                      {actionable && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              openSignoffModal(order.id, step.step, step.label, 'approve', step.assignee ?? undefined)
                                            }
                                            className="btn-secondary text-[10px]"
                                            disabled={approveSignoff.isPending}
                                          >
                                            簽核
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              openSignoffModal(order.id, step.step, step.label, 'reject', step.assignee ?? undefined)
                                            }
                                            className="btn-secondary text-[10px]"
                                            disabled={rejectSignoff.isPending}
                                          >
                                            退回
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                            <span>
                              交班狀態：{pendingHandoff ? '待接班' : latestHandoff ? '已接班' : '尚未交班'}
                            </span>
                            {latestHandoff && (
                              <span>
                                最近：{latestHandoff.fromRole} → {latestHandoff.toRole}
                              </span>
                            )}
                            {hasHandoffPermission && (
                              <button
                                type="button"
                                onClick={() => openHandoffModal(order.id, order.assignedRole ?? latestHandoff?.toRole ?? undefined)}
                                className="btn-secondary text-[10px]"
                                disabled={createHandoff.isPending}
                              >
                                交班
                              </button>
                            )}
                            {pendingHandoff && hasHandoffPermission && (
                              <button
                                type="button"
                                onClick={() => handleAcceptHandoff(order.id, pendingHandoff.id)}
                                className="btn-secondary text-[10px]"
                                disabled={acceptHandoff.isPending}
                              >
                                接班
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => setHistoryOrderId(order.id)}
                            className="btn-secondary text-xs"
                          >
                            📋 歷史
                          </button>
                          {order.status === 'open' && hasPermission('care:execute') && (
                            <button
                              type="button"
                              onClick={() => handleAcknowledge(order.id)}
                              className="btn-secondary text-xs"
                              disabled={acknowledgeOrder.isPending}
                            >
                              接單
                            </button>
                          )}
                          {order.status === 'acknowledged' && hasPermission('care:execute') && (
                            <button
                              type="button"
                              onClick={() => handleInProgress(order.id)}
                              className="btn-primary text-xs"
                              disabled={updateOrder.isPending}
                            >
                              開始執行
                            </button>
                          )}
                          {(order.status === 'acknowledged' || order.status === 'in_progress') && hasPermission('care:execute') && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleComplete(order.id)}
                                className="flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
                                disabled={completeOrder.isPending}
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                                完成
                              </button>
                              <button
                                type="button"
                                onClick={() => handleBlock(order.id)}
                                className="flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-40"
                                disabled={blockOrder.isPending}
                              >
                                <XCircleIcon className="h-4 w-4" />
                                封鎖
                              </button>
                            </>
                          )}
                          {order.status === 'blocked' && order.blockReason && (
                            <div className="rounded bg-red-50 p-2 text-xs text-red-700">
                              封鎖原因: {order.blockReason}
                            </div>
                          )}
                        </div>
                      </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ))}

          {orders.length === 0 && (
            <div className="glass-panel p-8 text-center">
              <p className="text-slate-500">目前沒有符合條件的指示</p>
            </div>
          )}
        </div>
      )}

      {/* Signoff Modal */}
      {signoffDialog && (
        <Modal
          isOpen={!!signoffDialog}
          onClose={() => setSignoffDialog(null)}
          title={`${signoffDialog.mode === 'approve' ? '簽核' : '退回'}：${signoffDialog.label}`}
          size="md"
        >
          <form onSubmit={handleSignoffSubmit} className="space-y-4 p-1">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">簽核意見</label>
              <textarea
                className="input text-sm min-h-[90px]"
                value={signoffNote}
                onChange={(e) => setSignoffNote(e.target.value)}
                placeholder={signoffDialog.mode === 'approve' ? '選填' : '退回原因必填'}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">自訂負責人（選填）</label>
              <input
                className="input text-sm"
                value={signoffAssignee}
                onChange={(e) => setSignoffAssignee(e.target.value)}
                placeholder="例：RN 王小美"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={() => setSignoffDialog(null)}>
                取消
              </button>
              <button
                type="submit"
                className="btn-primary text-sm"
                disabled={approveSignoff.isPending || rejectSignoff.isPending}
              >
                {signoffDialog.mode === 'approve'
                  ? approveSignoff.isPending
                    ? '簽核中...'
                    : '確認簽核'
                  : rejectSignoff.isPending
                    ? '退回中...'
                    : '退回'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Handoff Modal */}
      {handoffDialog && (
        <Modal
          isOpen={!!handoffDialog}
          onClose={() => setHandoffDialog(null)}
          title="交班資訊"
          size="md"
        >
          <form onSubmit={handleHandoffSubmit} className="space-y-4 p-1">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">交班對象角色</label>
              <input
                className="input text-sm"
                value={handoffRole}
                onChange={(e) => setHandoffRole(e.target.value)}
                placeholder={handoffDialog.suggestedRole || '例：ICU / 手術 / RN'}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">交班備註</label>
              <textarea
                className="input text-sm min-h-[90px]"
                value={handoffNote}
                onChange={(e) => setHandoffNote(e.target.value)}
                placeholder="描述交班內容、重點或資源需求"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={() => setHandoffDialog(null)}>
                取消
              </button>
              <button
                type="submit"
                className="btn-primary text-sm"
                disabled={createHandoff.isPending}
              >
                {createHandoff.isPending ? '送出中...' : '建立交班'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* History Modal */}
      {historyOrderId && (
        <CareOrderHistoryModal
          orderId={historyOrderId}
          isOpen={!!historyOrderId}
          onClose={() => setHistoryOrderId(null)}
        />
      )}
    </div>
  );
};

export default Tasks;
